'use client'

import React, { useState, useEffect } from 'react';
import clsx from 'clsx'; // A utility for conditionally constructing class names
import short from 'short-uuid'; // Import at the top level
import { IoCheckmarkDone } from "react-icons/io5";

// Define a cleaner Props interface
interface Props {
    message: any;
    isMe: boolean; // Pass this down as a prop for performance
}

// Wrap the component in React.memo for performance optimization
const SingleMessage: React.FC<Props> = ({ message, isMe }) => {
    const [formattedTime, setFormattedTime] = useState('');

    // console.log('messages: ', message)

    useEffect(() => {
        const clientFormattedTime = new Date(message.updated_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        });
        setFormattedTime(clientFormattedTime);
    }, [message.updated_at]); 

    // A more robust and readable async download function
    const downloadFile = async (filePath: string, contentType: string) => {
        const link = document.createElement('a');
        
        try {
            const response = await fetch(filePath, { method: 'GET', mode: "cors" });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            link.href = url;
            link.download = short.generate(); // Use the imported module
            
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url); // Clean up the object URL
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            // Ensure the link is removed from the DOM even if an error occurs
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        }
    };

    return (
        // Use clsx for clean, readable conditional classes
        <div className={clsx("flex items-start gap-2.5 mb-1", { "flex-row-reverse": isMe })}>
            <img className="w-8 h-8 rounded-full" src={message.icon} alt="User Icon" />
            
            <div className={clsx(
                "flex flex-col w-64 max-w-[320px] leading-1.5 p-4",
                isMe 
                    ? "rounded-s-xl rounded-ee-xl bg-blue-600" 
                    : "rounded-e-xl rounded-es-xl bg-gray-100 dark:bg-gray-700"
            )}>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <span className={clsx("text-sm font-semibold", isMe ? "text-white" : "text-gray-900 dark:text-white")}>
                        {message.user.charAt(0).toUpperCase() + message.user.slice(1)}
                    </span>
                    <span className={clsx("text-sm font-normal", isMe ? "text-blue-200" : "text-gray-500 dark:text-gray-400")}>
                        {formattedTime}
                    </span>
                </div>

                {message.type === 'picture' && (
                    <div className="group relative my-2.5">
                        <div className="absolute w-full h-full bg-gray-900/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                            <button onClick={() => downloadFile(message.file, 'image/jpeg')} className="inline-flex items-center justify-center rounded-full h-10 w-10 bg-white/30 hover:bg-white/50 focus:ring-4 focus:outline-none dark:text-white focus:ring-gray-50">
                                <svg className="w-5 h-5 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 18">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 1v11m0 0 4-4m-4 4L4 8m11 4v3a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-3"/>
                                </svg>
                            </button>
                        </div>
                        <img src={message.file} className="rounded-lg" alt="User attachment" />
                    </div>
                )}

                <div className="justify-items-end items-end" dir="ltr">
                    <p className={clsx("text-sm font-normal py-2.5", isMe ? "text-white" : "text-gray-900 dark:text-white")}>
                        {message.message}
                    </p>
                </div>

                {isMe && (
                    <span className="flex items-center gap-1 text-xs font-normal text-white self-end">
                        <IoCheckmarkDone />
                        <span>Delivered</span>
                    </span>
                )}
            </div>
        </div>
    );
};

export default React.memo(SingleMessage);